/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { ChevronDown, Settings, Copy, FileDown, Save, ArrowLeft, Maximize2 } from 'lucide-react'
import type { PeerConfig } from '@/lib/storage'
import type { PeerResponse } from '@/ui/stores/orchestrator-store'
import { OPENROUTER_MODELS } from '@/providers/llm-provider'
import { GeminiLogo, ClaudeLogo, GrokLogo } from '@/ui/components/LLMLogos'
import { Brain } from 'lucide-react'

interface PeerPanelProps {
  peer: PeerConfig
  peerResponse: PeerResponse | null
  height: number
  isOrchestrating: boolean
  draggedPeer: string | null
  onToggleCollapse: () => void
  onToggleEnabled: () => void
  onOpenSettings: () => void
  onCopy: () => void
  onInflate: () => void
  onSave: () => void
  onSendToOrchestrator: () => void
  onExpand: () => void
  onDragStart: (e: React.DragEvent) => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent) => void
  onDragEnd: () => void
  onResizeStart: () => void
}

export function PeerPanel({
  peer,
  peerResponse,
  height,
  isOrchestrating,
  draggedPeer,
  onToggleCollapse,
  onToggleEnabled,
  onOpenSettings,
  onCopy,
  onInflate,
  onSave,
  onSendToOrchestrator,
  onExpand,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onResizeStart,
}: PeerPanelProps) {
  // Get icon component for peer
  const getPeerIcon = (iconName: string) => {
    switch (iconName) {
      case 'gemini': return GeminiLogo
      case 'claude': return ClaudeLogo
      case 'grok': return GrokLogo
      default: return Brain
    }
  }

  // Get model display name
  const getModelName = (modelId: string | undefined): string => {
    if (!modelId) return ''
    const model = OPENROUTER_MODELS.find(m => m.id === modelId)
    return model ? model.name : modelId
  }

  const Logo = getPeerIcon(peer.icon)

  return (
    <div
      style={{
        height: peer.collapsed ? 'auto' : `${height}px`,
        opacity: peer.enabled ? (draggedPeer === peer.id ? 0.5 : 1) : 0.4,
        transform: draggedPeer === peer.id ? 'scale(0.98)' : 'scale(1)',
      }}
      className="bento-tile flex flex-col transition-all duration-200 ease-in-out"
      draggable={peer.enabled}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      <div className="bento-tile-header cursor-move">
        {/* Collapse/Expand Button - Top Left */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onToggleCollapse()
          }}
          className="p-1 hover:bg-background-tertiary rounded transition-colors mr-1"
          title={peer.collapsed ? 'Expand peer panel to see response' : 'Collapse peer panel to minimize'}
        >
          <ChevronDown className={`w-3 h-3 transition-transform ${peer.collapsed ? '-rotate-90' : ''}`} />
        </button>

        <Logo className={`w-4 h-4 ${peer.color}`} />
        <div className="flex flex-col items-start ml-2">
          <span className="text-xs font-semibold">{peer.personaName}</span>
          <span className="text-[10px] text-foreground-muted">{getModelName(peer.model)}</span>
        </div>

        <div className="ml-auto flex items-center gap-1">
          {/* Enable/Disable Toggle Switch */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleEnabled()
            }}
            className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
              peer.enabled ? 'bg-accent' : 'bg-background-tertiary'
            }`}
            title={peer.enabled ? 'Disable peer' : 'Enable peer'}
          >
            <span
              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                peer.enabled ? 'translate-x-3.5' : 'translate-x-0.5'
              }`}
            />
          </button>

          {/* Settings Gear */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              onOpenSettings()
            }}
            className="p-1 hover:bg-background-tertiary rounded transition-colors"
            title="Peer Settings"
          >
            <Settings className="w-3 h-3" />
          </button>

          {isOrchestrating && !peerResponse && peer.enabled && (
            <span className="text-accent animate-pulse">●</span>
          )}

          {peerResponse && (
            <>
              <button
                onClick={onCopy}
                className="p-1 hover:bg-background-tertiary rounded transition-colors"
                title="Copy to clipboard"
              >
                <Copy className="w-3 h-3" />
              </button>
              <button
                onClick={onInflate}
                className="p-1 hover:bg-background-tertiary rounded transition-colors"
                title="Inflate to Markdown"
              >
                <FileDown className="w-3 h-3" />
              </button>
              <button
                onClick={onSave}
                className="p-1 hover:bg-background-tertiary rounded transition-colors"
                title="Save as MOTH"
              >
                <Save className="w-3 h-3" />
              </button>
              <button
                onClick={onSendToOrchestrator}
                className="p-1 hover:bg-background-tertiary rounded transition-colors"
                title="Send to Orchestrator"
              >
                <ArrowLeft className="w-3 h-3" />
              </button>
            </>
          )}

          <button
            onClick={onExpand}
            className="p-1 hover:bg-background-tertiary rounded transition-colors"
            title="Expand"
          >
            <Maximize2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      {!peer.collapsed && (
        <>
          <div className={`flex-1 p-3 text-xs overflow-auto ${!peer.enabled ? 'pointer-events-none' : ''}`}>
            {peerResponse ? (
              <p className="whitespace-pre-wrap font-mono">{peerResponse.content}</p>
            ) : isOrchestrating && peer.enabled ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className={`${peer.color} animate-pulse text-4xl mb-2`}>●</div>
                  <p className="text-foreground-muted text-xs">Waiting for LLM...</p>
                </div>
              </div>
            ) : (
              <p className="text-foreground-muted italic">{peer.enabled ? 'No response yet' : 'Peer disabled'}</p>
            )}
          </div>
          <div
            className={`h-2 cursor-row-resize bg-border/30 hover:bg-accent/40 active:bg-accent/60 transition-colors flex items-center justify-center ${!peer.enabled ? 'pointer-events-none' : ''}`}
            onMouseDown={(e) => {
              e.preventDefault()
              onResizeStart()
            }}
            title="Drag to resize"
          >
            <div className="w-8 h-0.5 bg-foreground-muted/40 rounded-full" />
          </div>
        </>
      )}
    </div>
  )
}

